/**
 * Wayfinding: the skip link, the breadcrumb trail, and route-driven tabs.
 *
 * The application had none of these. Back-navigation was implemented four
 * different ways, and there was no way to reach the content past the nav with
 * a keyboard.
 *
 * @module components/ui/Navigation
 */

import React, { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { Link, NavLink, useLocation } from "react-router";

import { Badge } from "./Badge";
import { FloatingTip } from "./FloatingTip";
import { Icon } from "./Icon";

export interface SkipLinkProps {
  /** The id of the element that holds the page content. */
  targetId?: string;
  children?: ReactNode;
  /** For a shell that has to take this out of the focus order with its target. */
  ref?: React.Ref<HTMLAnchorElement>;
}

/**
 * The first thing in the tab order, and invisible until it is reached.
 *
 * Without it every keyboard user walks the whole sidebar and top bar before
 * reaching the content, on every page.
 */
export function SkipLink({
  targetId = "main-content",
  children = "Skip to main content",
  ref = undefined,
}: SkipLinkProps): React.ReactElement {
  return (
    <a
      ref={ref}
      href={`#${targetId}`}
      // The padding is focus-scoped as well: `focus:not-sr-only` sets
      // `padding: 0` at a specificity `px-4` cannot reach, so the visible
      // link was bare text inside its own border.
      className="sr-only rounded-box border border-base-300 bg-base-100 px-4 py-2 text-sm font-semibold focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2"
    >
      {children}
    </a>
  );
}

export interface Crumb {
  label: string;
  /** Omit on the last crumb: the page you are on is not a link to itself. */
  to?: string;
  /** The label is a stand-in until its data arrives; the crumb holds its width. */
  pending?: boolean;
}

export interface BreadcrumbProps {
  items: readonly Crumb[];
  className?: string;
  "data-testid"?: string;
}

/** A stand-in the width of a typical name, so the trail keeps its length when the real one arrives. */
function content(item: Crumb): ReactNode {
  return item.pending ? (
    <span className="inline-block h-3 w-[16ch] max-w-full rounded bg-base-300 align-middle">
      <span className="sr-only">{item.label}</span>
    </span>
  ) : (
    item.label
  );
}

/**
 * Where this page sits. Ancestors collapse below `sm`, leaving the current
 * page, because a three-level trail on a phone is longer than the title.
 */
export function Breadcrumb({
  items,
  className = "",
  "data-testid": testId,
}: BreadcrumbProps): React.ReactElement {
  return (
    <nav aria-label="Breadcrumb" className={className || undefined} data-testid={testId}>
      <ol className="flex min-w-0 flex-nowrap items-center gap-1 text-md">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            // Keyed by address as well as label: the trail names whatever the
            // routes named, and a programme titled "Programmes" collides with
            // the ancestor crumb above it. Only the last crumb omits a `to`,
            // so the pair is unique.
            <li
              key={`${item.to ?? ""}:${item.label}`}
              className={`flex items-center gap-1 ${last ? "min-w-0" : "shrink-0 max-sm:hidden"}`}
            >
              {index > 0 && (
                <Icon
                  name="caret-right"
                  aria-hidden
                  className="shrink-0 text-muted max-sm:hidden"
                />
              )}
              {last || !item.to ? (
                <span
                  // One line: the topbar keeps its height, and the full name is
                  // on hover.
                  title={item.label}
                  className={
                    last
                      ? "block truncate font-semibold text-base-content"
                      : "text-muted max-sm:hidden"
                  }
                  aria-current={last ? "page" : undefined}
                >
                  {content(item)}
                </span>
              ) : (
                // Link, not NavLink: NavLink marks an active ancestor
                // aria-current="page", and on /programmes/:id that gives the
                // trail two current pages. A crumb behind the last one is by
                // definition not where you are.
                <Link to={item.to} className="text-muted hover:text-base-content max-sm:hidden">
                  {content(item)}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export interface LinkTabItem {
  to: string;
  label: string;
  icon?: string;
  badge?: ReactNode;
  /** What the badge counts, in full ("3 learning outcomes"), for a reader who cannot see the chip. */
  badgeLabel?: string;
  /** Matches only the exact path, for an index route. */
  end?: boolean;
  /** Why it cannot be opened yet. Present means the tab does not navigate. */
  lockedReason?: string;
  /** A step other tabs wait on that is not finished: marked, still a link. */
  incomplete?: boolean;
  "data-testid"?: string;
}

/** The mask that softens whichever edge of a scrolled strip is clipped. */
function fadeFor(start: boolean, end: boolean): string {
  if (start && end) {
    return "[mask-image:linear-gradient(to_right,transparent,black_2rem,black_calc(100%-2rem),transparent)]";
  }
  if (start) {
    return "[mask-image:linear-gradient(to_right,transparent,black_2rem)]";
  }
  return end ? "[mask-image:linear-gradient(to_left,transparent,black_2rem)]" : "";
}

export interface LinkTabsProps {
  items: readonly LinkTabItem[];
  "aria-label": string;
  /**
   * One row at every width, scrolled sideways with a fade at a clipped edge
   * rather than wrapped, for a strip that sticks: wrapped, a long strip took
   * half a phone screen with it and re-wrapped as the selection changed.
   */
  oneRow?: boolean;
  className?: string;
  "data-testid"?: string;
}

/**
 * Tabs that are routes.
 *
 * Deliberately links rather than `role="tab"`: each one changes the URL, and a
 * tab that navigates is a link wearing a tab's clothes. `aria-current` carries
 * the selection, which is what a link is supposed to use.
 */
export function LinkTabs({
  items,
  "aria-label": label,
  oneRow = false,
  className = "",
  "data-testid": testId,
}: LinkTabsProps): React.ReactElement {
  // A route match ignores the hash, so tabs that differ only by one would all
  // be current. The hash decides between them.
  const { pathname, hash } = useLocation();
  const listRef = useRef<HTMLUListElement>(null);
  const [clipped, setClipped] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    const start = list.scrollLeft > 1;
    const end = list.scrollLeft + list.clientWidth < list.scrollWidth - 1;
    setClipped((current) =>
      current.start === start && current.end === end ? current : { start, end },
    );
  }, []);

  // The current tab scrolled into the row, or a scrolled strip can open on a
  // section whose tab is off screen. Only the row moves, never the page.
  useEffect(() => {
    const list = listRef.current;
    const current = list?.querySelector<HTMLElement>('[aria-current="page"]')?.parentElement;
    if (oneRow && list && current && list.scrollWidth > list.clientWidth) {
      list.scrollLeft = current.offsetLeft - (list.clientWidth - current.offsetWidth) / 2;
    }
    if (oneRow) {
      measure();
    }
  }, [oneRow, pathname, hash, measure]);

  useEffect(() => {
    const list = listRef.current;
    if (!oneRow || !list || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => {
      observer.disconnect();
    };
  }, [oneRow, measure]);

  const fade = fadeFor(clipped.start, clipped.end);

  const hashOf = (to: string) => (to.includes("#") ? to.slice(to.indexOf("#")) : "");
  const tabHashes = new Set(items.map((item) => hashOf(item.to)).filter(Boolean));
  const holds = (to: string) => {
    const own = hashOf(to);
    return own ? own === hash : !tabHashes.has(hash);
  };
  return (
    <nav aria-label={label} className={className || undefined} data-testid={testId}>
      <ul
        ref={listRef}
        onScroll={oneRow ? measure : undefined}
        className={`flex items-center gap-1 border-b border-base-300${
          oneRow
            ? ` relative flex-nowrap overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>li]:shrink-0 [&>li]:whitespace-nowrap ${fade}`
            : " flex-wrap"
        }`}
      >
        {items.map((item) =>
          item.lockedReason ? (
            // A disabled link with no href, focusable so the tooltip opens on
            // keyboard focus as well as hover; a `title` shows late or never.
            // The sr-only reason already describes it, so no aria-describedby.
            <li key={item.to}>
              <FloatingTip tip={item.lockedReason}>
                {({ ref, onMouseEnter, onMouseLeave, onFocus, onBlur }) => (
                  <span
                    ref={ref}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    role="link"
                    tabIndex={0}
                    aria-disabled="true"
                    data-testid={item["data-testid"]}
                    className="inline-flex cursor-not-allowed items-center gap-2 border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-light"
                  >
                    <Icon name="lock-simple" aria-hidden />
                    {item.label} <span className="sr-only">(locked: {item.lockedReason})</span>
                  </span>
                )}
              </FloatingTip>
            </li>
          ) : (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                aria-current={holds(item.to) ? "page" : "false"}
                data-testid={item["data-testid"]}
                className={({ isActive }) =>
                  [
                    "inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium",
                    isActive && holds(item.to)
                      ? // Weight is drawn with a stroke, not a bolder face: a bolder face
                        // is wider, so selecting a tab reflowed the whole strip.
                        "border-primary text-base-content [-webkit-text-stroke:0.5px]"
                      : "border-transparent text-muted hover:text-base-content",
                  ].join(" ")
                }
              >
                {item.incomplete ? (
                  <>
                    <Icon name="circle-dashed" className="text-warning-strong" aria-hidden />
                    {/* A flex container drops a bare space on screen but keeps it in the name. */}
                    <span className="sr-only">(incomplete)</span>{" "}
                  </>
                ) : null}
                {item.icon && <Icon name={item.icon} aria-hidden />}
                {/* Both in elements of their own. Two adjacent text nodes are
                    one anonymous flex item, so the row's `gap` separated
                    nothing, and a text node run against an element is
                    concatenated with no space into the link's name: the count
                    arrived as "Outcomes2" on screen and to a reader. */}
                <span>{item.label}</span>
                {item.badge !== undefined && item.badge !== null ? (
                  <>
                    {/* A flex container does not render an anonymous item of
                        pure white space, so this separates the two in the
                        name and takes no width on screen. */}{" "}
                    <Badge size="xs" aria-label={item.badgeLabel}>
                      {item.badge}
                    </Badge>
                  </>
                ) : null}
              </NavLink>
            </li>
          ),
        )}
      </ul>
    </nav>
  );
}
